import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLabBasket } from '../../contexts/LabBasketContext';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../theme/laboklinTheme';

/**
 * The tests picked up while reading the answer, and the way out to an order.
 *
 * Sits above the conversation and appears only once something is in it, so an
 * ordinary interpretation is unaffected. Each entry was added from the sentence
 * that argued for it, which is the point: the veterinarian assembles the basket as
 * they read the reasoning, not from a recommendation list at the end.
 *
 * Nothing here places an order. "Confirm order" hands over to Laboklin's own order
 * form, where the veterinarian confirms it.
 */
const LabBasketStrip: React.FC = () => {
  const basket = useLabBasket();
  const navigate = useNavigate();

  if (!basket || basket.items.length === 0) return null;

  const { items, total, remove } = basket;

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${LABOKLIN_BORDER}`,
        bgcolor: 'rgba(0,0,0,0.015)',
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: LABOKLIN_RED,
          flexShrink: 0,
        }}
      >
        Order · {items.length}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flexGrow: 1 }}>
        {items.map((item) => (
          <Chip
            key={item.serviceNo}
            size="small"
            onDelete={() => remove(item.serviceNo)}
            label={
              item.price !== null
                ? `${item.name} · €${item.price.toFixed(2)}`
                : item.name
            }
            sx={{ fontSize: 12, bgcolor: 'background.paper' }}
          />
        ))}
      </Box>

      {total !== null && (
        <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          €{total.toFixed(2)}
        </Typography>
      )}

      <Button
        size="small"
        variant="contained"
        onClick={() => navigate('/mylab/order/new')}
        sx={{
          textTransform: 'none',
          flexShrink: 0,
          fontWeight: 600,
          bgcolor: LABOKLIN_RED,
          '&:hover': { bgcolor: LABOKLIN_RED, filter: 'brightness(0.9)' },
        }}
      >
        Confirm order
      </Button>
    </Box>
  );
};

export default LabBasketStrip;
